import { FilePreview, Typography, Dropzone, Button } from '@ui/components';
import { ResponseErrorDto, OnboardingStep } from '@interfaces/models';
import { ComponentPropsWithoutRef, useState, FC } from 'react';
import { useMutation } from '@tanstack/react-query';
import { importCsvFile } from '@api/onboarding';
import { useTranslation } from 'react-i18next';
import { Success } from '@ui/components/icons';
import { Link } from 'react-router-dom';
import { Routes } from '@shared/enums';

type FileUploaderProps = Partial<ComponentPropsWithoutRef<typeof Dropzone>> & {
  onCancel: () => void;
};

export const FileUploader: FC<FileUploaderProps> = ({ className, onCancel }) => {
  const [apiErrorKey, setApiErrorKey] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);

  const { t } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: (file: File) => importCsvFile(file, OnboardingStep.IMPORT_SUMMARY),
    onMutate: () => {
      setApiErrorKey(null);
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      setApiErrorKey(`API_ERRORS.${error.errorCode}`);
      setFile(null);
    },
  });

  const handleUpload = (file: File | null) => {
    if (!file) return;
    setFile(file);
    mutate(file);
  };

  return (
    <>
      {file && !isPending ? (
        <>
          <FilePreview
            actions={
              <Button onClick={() => setFile(null)} variant="secondary" size="small">
                {t('ONBOARDING.IMPORT_MODAL.UPLOADER.REPLACE_FILE')}
              </Button>
            }
            file={file}
          />

          <div className="text-success flex items-center gap-2 pl-4">
            <Success className="size-4" />
            <Typography variant="h100">
              {t('ONBOARDING.IMPORT_MODAL.UPLOADER.FILE_LOADED_SUCCESSFULLY')}
            </Typography>
          </div>

          <div className="flex gap-4">
            <Button variant="tertiary" onClick={onCancel} size="large" wide>
              {t('ONBOARDING.IMPORT_MODAL.CANCEL')}
            </Button>

            <Button variant="primary" size="large" asChild wide>
              <Link to={`../${Routes.IMPORT_SUMMARY}`} replace>
                {t('ONBOARDING.IMPORT_MODAL.UPLOAD')}
              </Link>
            </Button>
          </div>
        </>
      ) : (
        <Dropzone
          multipleFilesUploadedErrorMessage={t(
            'ONBOARDING.IMPORT_MODAL.UPLOADER.MULTIPLE_FILES_UPLOADED_ERROR_MESSAGE',
          )}
          invalidFileFormatMessage={t(
            'ONBOARDING.IMPORT_MODAL.UPLOADER.INVALID_FILE_FORMAT_MESSAGE',
          )}
          fileTooLargeMessage={t('ONBOARDING.IMPORT_MODAL.UPLOADER.FILE_TOO_LARGE_MESSAGE')}
          loadingDescription={t('ONBOARDING.IMPORT_MODAL.UPLOADER.LOADING_DESCRIPTION')}
          uploadDescription={t('ONBOARDING.IMPORT_MODAL.UPLOADER.OR_DRAG_AND_DROP')}
          uploadLabel={t('ONBOARDING.IMPORT_MODAL.UPLOADER.CLICK_TO_UPLOAD')}
          loadingLabel={t('ONBOARDING.IMPORT_MODAL.UPLOADER.LOADING_LABEL')}
          errorLabel={t('ONBOARDING.IMPORT_MODAL.UPLOADER.ERROR_TITLE')}
          errorMessage={!!apiErrorKey && t(apiErrorKey)}
          acceptedFormats={['csv', 'CSV']}
          onFileSelect={handleUpload}
          isLoading={isPending}
          className={className}
          maxSizeMB={10}
          size="large"
        />
      )}
    </>
  );
};
